/**
 * One Inspector run, read as a story: what was asked, what Inspector did
 * step by step, and what came out of it.
 *
 * The page is flat — no card around the whole thing. The run's *timeline*
 * is one continuous rail from the request to the end, with every progress
 * note written out in full where it happened, so "the steps" are simply the
 * things Inspector said as it worked. The *report* (or, for a finding
 * review, the response) is the other tab. Facts about the run sit in a small
 * card beside it.
 *
 * Built only from the workspace's own primitives: the page shell, animated
 * tabs, the timeline rail, cards, alerts and collapsibles.
 *
 * @module components/regulation/inspector/InspectionRunWorkspace
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleDashedIcon,
  HistoryIcon,
  MessageSquareIcon,
  PauseIcon,
  PlayIcon,
  XIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import ChatMarkdown from "../../ChatMarkdown";
import { InspectionRunAlert } from "./InspectionRunAlert";
import {
  AnimatedTabs,
  AnimatedTabsContent,
  AnimatedTabsList,
  AnimatedTabsTrigger,
} from "../../ui/animated-tabs";
import { Button } from "../../ui/button";
import { Card, CardFooter, CardHeader, CardPanel, CardTitle } from "../../ui/card";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../../ui/collapsible";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../../ui/empty";
import { Spinner } from "../../ui/spinner";
import { Timeline, TimelineItem } from "../../ui/timeline";
import { useInspections } from "../../../state/inspections";
import { cn } from "~/lib/utils";
import { InspectionResults } from "./InspectionResults";
import { InspectionStatusBadge } from "./InspectionStatus";
import {
  formatDuration,
  inspectionActive,
  inspectionTitle,
  progressSteps,
  TRIGGER_LABEL,
  type InspectionDetail,
} from "./inspectorView";
import { findingRequestHistory, inspectionStepSummary } from "./runPresentation";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
const time = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/** Notes longer than this fold behind their first line. */
const NOTE_FOLD_CHARS = 700;

/**
 * One progress note on the rail: its first line as the title, the rest
 * written out beneath. A long note folds, so the rail stays scannable.
 */
function StepNote({ text, createdAt }: { readonly text: string; readonly createdAt: string }) {
  const title = inspectionStepSummary(text);
  const lines = text.trim().split("\n");
  const rest = lines.slice(1).join("\n").trim();
  const body = rest.length > 0 ? rest : null;
  const folded = body !== null && body.length > NOTE_FOLD_CHARS;
  return (
    <div className="pt-1">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 break-words text-sm font-medium leading-6">{title}</p>
        <time dateTime={createdAt} className="shrink-0 font-mono text-[11px] text-muted-foreground">
          {time(createdAt)}
        </time>
      </div>
      {body === null ? null : folded ? (
        <Collapsible>
          <CollapsibleTrigger className="group mt-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            Read the full note
            <ChevronDownIcon
              aria-hidden
              className="size-3.5 transition-transform duration-200 ease-out group-data-panel-open:rotate-180 motion-reduce:transition-none"
            />
          </CollapsibleTrigger>
          <CollapsiblePanel className="motion-reduce:transition-none">
            <div className="pt-2 text-sm leading-6 text-muted-foreground">
              <ChatMarkdown text={body} cwd={undefined} className="text-sm" />
            </div>
          </CollapsiblePanel>
        </Collapsible>
      ) : (
        <div className="mt-1 text-sm leading-6 text-muted-foreground">
          <ChatMarkdown text={body} cwd={undefined} className="text-sm" />
        </div>
      )}
    </div>
  );
}

/** A label/value row in the facts card. */
function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-xs">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium tabular-nums">{children}</dd>
    </div>
  );
}

export function InspectionRunWorkspace({
  detail,
  orgSegment,
  organizationId,
}: {
  detail: InspectionDetail;
  orgSegment: string;
  organizationId: string | null;
}) {
  const { run, items } = detail;
  const active = inspectionActive(run);
  const report = run.status === "completed" ? detail.report : null;
  // For a review the response is the last note, so keep every assistant
  // message; only a library report is peeled off the steps here.
  const steps = progressSteps({ ...detail, report: run.review ? null : report });
  // A finding review's answer is its last note; a library run's is its report.
  const response = run.review && run.status === "completed" ? steps.at(-1) : null;
  const notes = response ? steps.slice(0, -1) : steps;
  const latest = notes.at(-1);
  const resultLabel = run.review ? "Response" : "Report";
  // While Inspector works, the timeline is the page; once it has answered,
  // the answer is.
  const [tab, setTab] = useState(active ? "timeline" : "result");
  const history = useInspections(run.review ? organizationId : null, run.review?.findingId);
  const requests = findingRequestHistory(history.snapshot?.runs ?? [], run);
  const newer = requests.find((item) => Date.parse(item.startedAt) > Date.parse(run.startedAt));

  const EndIcon =
    run.status === "completed" ? CheckIcon : run.status === "failed" ? XIcon : PauseIcon;
  const endTitle =
    run.status === "completed"
      ? run.review
        ? "Response ready"
        : report
          ? "Report ready"
          : "Finished"
      : run.status === "failed"
        ? "Could not finish"
        : "Stopped";
  const requestedBy = run.review
    ? "Asked from the finding's conversation"
    : run.trigger
      ? TRIGGER_LABEL[run.trigger]
      : "Inspection";
  const recorded = [
    [run.found.findings, "finding", "findings"],
    [run.found.suggestions, "suggestion", "suggestions"],
    [run.found.publications, "publication", "publications"],
    [run.found.revised, "revision", "revisions"],
    [run.found.withdrawn, "withdrawal", "withdrawals"],
  ] as const;

  return (
    <div
      data-testid="inspection-run"
      className="grid min-w-0 items-start gap-8 text-foreground xl:grid-cols-[minmax(0,1fr)_17rem]"
    >
      <div className="min-w-0 space-y-6">
        {run.review ? (
          <section aria-label="Request" className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              <MessageSquareIcon aria-hidden className="size-3.5" />
              Your team asked
              <time dateTime={run.startedAt} className="ml-auto font-mono text-[11px]">
                {when(run.startedAt)}
              </time>
            </div>
            <blockquote className="border-l-2 border-border pl-4 text-sm leading-6 break-words whitespace-pre-wrap">
              {inspectionTitle(run)}
            </blockquote>
            <Link
              to="/$org/compliance/findings/$findingId"
              params={{ org: orgSegment, findingId: run.review.findingId }}
              search={{}}
              hash="finding-conversation"
              className="mt-2 inline-flex items-center gap-1 pl-4 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              View conversation
              <ArrowUpRightIcon aria-hidden className="size-3" />
            </Link>
          </section>
        ) : null}

        <InspectionRunAlert run={run} />

        <AnimatedTabs value={tab} onValueChange={(value) => setTab(String(value))}>
          <AnimatedTabsList aria-label="Inspector run">
            <AnimatedTabsTrigger value="result">{resultLabel}</AnimatedTabsTrigger>
            <AnimatedTabsTrigger value="timeline">
              Timeline
              <span className="text-xs font-normal tabular-nums text-muted-foreground">
                {notes.length}
              </span>
            </AnimatedTabsTrigger>
          </AnimatedTabsList>

          <AnimatedTabsContent value="result">
            <div className="py-5">
              {report ? (
                <section
                  aria-label={run.review ? "Inspector response" : "Inspector report"}
                  className="min-w-0 text-sm leading-7 [&_h1]:text-xl [&_h2]:text-lg"
                >
                  <ChatMarkdown text={report} cwd={undefined} className="text-sm" />
                </section>
              ) : response ? (
                <section aria-label="Inspector response" className="min-w-0 text-sm leading-7">
                  <ChatMarkdown text={response.text} cwd={undefined} className="text-sm" />
                </section>
              ) : latest ? (
                <section aria-label="Latest note">
                  <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <span
                      aria-hidden
                      className={cn(
                        "size-1.5 rounded-full",
                        active
                          ? "bg-primary animate-pulse motion-reduce:animate-none"
                          : "bg-muted-foreground",
                      )}
                    />
                    {active ? "Latest note" : "Last note before it ended"}
                    <time dateTime={latest.createdAt} className="ml-auto font-mono text-[11px]">
                      {time(latest.createdAt)}
                    </time>
                  </div>
                  <ChatMarkdown text={latest.text} cwd={undefined} className="text-sm" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3 -ml-2"
                    onClick={() => setTab("timeline")}
                  >
                    See the whole timeline
                    <ArrowUpRightIcon aria-hidden className="size-3.5" />
                  </Button>
                </section>
              ) : (
                <Empty className="py-8 md:p-8">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      {active ? <Spinner /> : <CircleDashedIcon aria-hidden />}
                    </EmptyMedia>
                    <EmptyTitle>
                      {active ? "Waiting for Inspector’s first note" : "Nothing was written"}
                    </EmptyTitle>
                    <EmptyDescription>
                      {active
                        ? "You can leave this page. The run keeps going, and its answer lands in the finding's conversation."
                        : "This run ended before Inspector wrote anything."}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              {!run.review ? (
                <div className="mt-8">
                  <InspectionResults
                    items={items}
                    orgSegment={orgSegment}
                    completed={run.status === "completed"}
                  />
                </div>
              ) : null}
            </div>
          </AnimatedTabsContent>

          <AnimatedTabsContent value="timeline">
            <section aria-label="Run timeline" className="py-5">
              <Timeline>
                <TimelineItem
                  indicator={<PlayIcon aria-hidden className="size-3.5 text-muted-foreground" />}
                >
                  <div className="flex items-baseline justify-between gap-3 pt-1">
                    <p className="text-sm font-medium leading-6">Requested</p>
                    <time
                      dateTime={run.startedAt}
                      className="shrink-0 font-mono text-[11px] text-muted-foreground"
                    >
                      {time(run.startedAt)}
                    </time>
                  </div>
                  <p className="mt-0.5 text-sm leading-6 text-muted-foreground">{requestedBy}</p>
                </TimelineItem>
                {notes.map((step, index) => (
                  <TimelineItem
                    key={`${step.createdAt}:${index}`}
                    indicator={
                      <span className="flex size-6 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-medium tabular-nums text-muted-foreground">
                        {index + 1}
                      </span>
                    }
                  >
                    <StepNote text={step.text} createdAt={step.createdAt} />
                  </TimelineItem>
                ))}
                {active ? (
                  <TimelineItem indicator={<Spinner className="size-4 text-primary" />}>
                    <div className="flex items-baseline justify-between gap-3 pt-1">
                      <p className="text-sm font-medium leading-6">
                        {run.status === "queued" ? "Waiting to start" : "Inspector is working"}
                      </p>
                      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                        {run.status === "queued" ? "" : formatDuration(run.startedAt, null)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
                      {run.status === "queued"
                        ? "The request is queued. Notes appear here as Inspector works."
                        : "New notes appear here as they are written. You can leave this page."}
                    </p>
                  </TimelineItem>
                ) : (
                  <TimelineItem
                    indicator={
                      <EndIcon
                        aria-hidden
                        className={cn(
                          "size-4",
                          run.status === "completed"
                            ? "text-success"
                            : run.status === "failed"
                              ? "text-destructive"
                              : "text-warning",
                        )}
                      />
                    }
                  >
                    <div className="flex items-baseline justify-between gap-3 pt-1">
                      <p className="text-sm font-medium leading-6">{endTitle}</p>
                      {run.completedAt ? (
                        <time
                          dateTime={run.completedAt}
                          className="shrink-0 font-mono text-[11px] text-muted-foreground"
                        >
                          {time(run.completedAt)}
                        </time>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
                      {run.completedAt
                        ? `${formatDuration(run.startedAt, run.completedAt)} in all.`
                        : "The end time was not recorded."}
                      {run.status === "completed" && (report || response) ? (
                        <>
                          {" "}
                          <button
                            type="button"
                            className="font-medium text-foreground underline-offset-4 hover:underline"
                            onClick={() => setTab("result")}
                          >
                            Read the {resultLabel.toLowerCase()}
                          </button>
                          .
                        </>
                      ) : null}
                    </p>
                  </TimelineItem>
                )}
              </Timeline>
            </section>
          </AnimatedTabsContent>
        </AnimatedTabs>
      </div>

      <aside aria-label="About this run" className="min-w-0 space-y-4 xl:sticky xl:top-6">
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">About this run</CardTitle>
          </CardHeader>
          <CardPanel className="pt-3">
            <dl className="divide-y divide-border/60">
              <Fact label="Type">{run.review ? "Finding review" : requestedBy}</Fact>
              <Fact label="Requested">{when(run.startedAt)}</Fact>
              <Fact label={active ? "Elapsed" : "Took"}>
                {run.status === "queued"
                  ? "Not started"
                  : run.completedAt || active
                    ? formatDuration(run.startedAt, run.completedAt)
                    : "Not recorded"}
              </Fact>
              <Fact label="Notes">{notes.length}</Fact>
              {!run.review ? (
                <Fact label="Recorded">
                  {recorded.some(([n]) => n > 0)
                    ? recorded
                        .filter(([n]) => n > 0)
                        .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`)
                        .join(", ")
                    : run.status === "completed"
                      ? "Nothing"
                      : "—"}
                </Fact>
              ) : null}
            </dl>
          </CardPanel>
        </Card>

        {run.review ? (
          <Card role="region" aria-label="Requests for this finding">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-sm">
                <HistoryIcon aria-hidden className="size-3.5 text-muted-foreground" />
                Requests for this finding
              </CardTitle>
            </CardHeader>
            <CardPanel className="pt-3">
              {newer ? (
                <Link
                  to="/$org/compliance/inspector/$threadId"
                  params={{ org: orgSegment, threadId: newer.threadId }}
                  className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs font-medium text-primary transition-colors duration-150 ease-out hover:bg-primary/10"
                >
                  A newer review is available
                  <ArrowUpRightIcon aria-hidden className="size-3.5 shrink-0" />
                </Link>
              ) : null}
              <ul className="-mx-1 space-y-0.5">
                {requests
                  .filter((item, index) => index < 5 || item.threadId === run.threadId)
                  .map((item) => (
                    <li key={item.threadId}>
                      <Link
                        to="/$org/compliance/inspector/$threadId"
                        params={{ org: orgSegment, threadId: item.threadId }}
                        aria-current={item.threadId === run.threadId ? "page" : undefined}
                        className="block rounded-lg px-3 py-2 transition-colors duration-150 ease-out hover:bg-muted/60 aria-[current=page]:bg-muted"
                      >
                        <span className="flex items-center justify-between gap-2">
                          <time
                            dateTime={item.startedAt}
                            className="text-[11px] text-muted-foreground"
                          >
                            {when(item.startedAt)}
                          </time>
                          <InspectionStatusBadge status={item.status} />
                        </span>
                        <span className="mt-1 line-clamp-2 break-words text-xs leading-5">
                          {inspectionTitle(item)}
                        </span>
                        {item.threadId === run.threadId ? (
                          <span className="mt-1 block text-[10px] font-medium text-muted-foreground">
                            Viewing this request
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
              </ul>
              {history.error ? (
                <div role="status" className="mt-2 text-xs text-muted-foreground">
                  Could not refresh other requests.
                  <Button size="xs" variant="ghost" className="ml-1" onClick={history.refresh}>
                    Retry
                  </Button>
                </div>
              ) : null}
            </CardPanel>
            <CardFooter>
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                render={
                  <Link
                    to="/$org/compliance/findings/$findingId"
                    params={{ org: orgSegment, findingId: run.review.findingId }}
                    search={{ compose: "inspector" }}
                    hash="finding-conversation"
                  />
                }
              >
                Ask a follow-up
                <ArrowUpRightIcon aria-hidden className="size-3.5" />
              </Button>
            </CardFooter>
          </Card>
        ) : null}
      </aside>
    </div>
  );
}
